export type BotUseCase = {
  id: string;
  title: string;
  icon: string;
  bullets: string[];
  accentColor: string;
  category?: "front" | "team";
  showOnLanding?: boolean;
  examplePrompt?: string;
};

export const botUseCases: BotUseCase[] = [
  {
    id: "shopify-store",
    title: "Shopify Store Automation",
    icon: "🛍️",
    bullets: [
      "Automates customer support and product recommendations 24/7",
      "Queries store catalog, inventory, and order status in real time",
      "Increases checkout conversions and reduces cart abandonment",
      "Best for: E-commerce brands wanting hands-free customer sales and support",
    ],
    accentColor: "text-blue-500",
    category: "front",
    examplePrompt:
      "Automate customer service for my Shopify store. Answer product questions, check order statuses, and help shoppers find what they need to checkout.",
  },
  {
    id: "personal-assistant",
    title: "Life & Work Assistant",
    icon: "🤵",
    bullets: [
      "Automates your daily communications, scheduling, and follow-ups",
      "Manages messages on WhatsApp and syncs with your Google Calendar",
      "Sends morning briefings, task reminders, and handles routine requests",
      "Best for: Busy founders and professionals automating their daily operations",
    ],
    accentColor: "text-green-500",
    category: "team",
    examplePrompt:
      "Automate my daily routine on WhatsApp. Keep track of my tasks, manage my calendar, send me daily morning briefs, and follow up with contacts.",
  },
  {
    id: "appointment-scheduler",
    title: "Automated Booking & Scheduling",
    icon: "📅",
    bullets: [
      "Books and manages client appointments automatically",
      "Answers questions about services and pricing before booking",
      "Syncs directly with Google Calendar or Calendly",
      "Best for: Consultants, clinics, service providers, and coaches",
    ],
    accentColor: "text-purple-500",
    category: "front",
    examplePrompt:
      "Automate appointment booking for my consulting business. Check my calendar availability, answer common client questions, and schedule sessions.",
  },
  {
    id: "knowledge-source",
    title: "Business Knowledge Base",
    icon: "📚",
    bullets: [
      "Automates answers directly from your website, docs, or PDFs",
      "Provides instant, accurate customer answers around the clock",
      "Embeds on your website or connects to WhatsApp and Telegram",
      "Best for: Businesses with extensive documentation, FAQs, or courses",
    ],
    accentColor: "text-orange-500",
    category: "front",
    examplePrompt:
      "Automate customer inquiries for our product. Crawl our website docs and accurately answer technical and onboarding questions 24/7.",
  },
  {
    id: "customer-success",
    title: "Customer Retention & Follow-ups",
    icon: "🤝",
    bullets: [
      "Proactively checks in with clients over email and chat",
      "Automates onboarding follow-ups, renewal nudges, and check-ins",
      "Answers customer questions instantly using your product knowledge",
      "Best for: SaaS and subscription businesses reducing churn on autopilot",
    ],
    accentColor: "text-cyan-500",
    category: "front",
    examplePrompt:
      "Automate client retention over email. Send proactive onboarding check-ins, handle common customer questions, and remind users before renewals.",
  },
  {
    id: "service-interface",
    title: "Backend Workflow & API Interface",
    icon: "🛠️",
    bullets: [
      "Connects your backend APIs to chat so routine actions happen automatically",
      "Automates order lookups, account status changes, and refunds via API",
      "Replaces repetitive manual admin work with automated actions",
      "Best for: Teams wanting conversational interfaces for their backend tools",
    ],
    accentColor: "text-sky-500",
    category: "front",
    examplePrompt:
      "Automate support actions for our app. Call our backend API to check subscription status, process refunds, and update account settings.",
  },
  {
    id: "therapy-self-help",
    title: "Daily Reflection & Wellness",
    icon: "🧘",
    bullets: [
      "Provides structured daily check-ins, habit tracking, and guided journaling",
      "Uses reflective listening and actionable personal prompts",
      "Non-clinical, privacy-first personal companion",
      "Best for: Daily personal organization, mindfulness, and habits",
    ],
    accentColor: "text-rose-500",
    category: "front",
    examplePrompt:
      "Help me stay on top of my daily wellness. Check in with me every evening, help me reflect on my day, and track my habits.",
  },
  {
    id: "leads-chat",
    title: "Lead Capture & Qualification",
    icon: "🎯",
    bullets: [
      "Engages website visitors instantly 24/7 to capture and qualify leads",
      "Asks qualifying questions and collects contact details",
      "Automatically books calls or alerts your sales team for hot leads",
      "Best for: Agencies and B2B businesses converting traffic into pipeline",
    ],
    accentColor: "text-amber-500",
    category: "front",
    examplePrompt:
      "Automate lead capture on our website. Greet visitors, ask qualifying questions about their budget and timeline, collect their email, and book an intro call.",
  },
  {
    id: "coder-integrator",
    title: "Custom Integrations & Coding",
    icon: "💻",
    bullets: [
      "An AI developer that builds and hosts custom automations for you",
      "Connects APIs, sets up webhooks, scrapes data, and syncs across platforms",
      "Runs inside a persistent cloud VM with complete developer tooling",
      "Best for: Anyone wanting custom multi-tool automations built without writing code",
    ],
    accentColor: "text-indigo-500",
    category: "team",
    examplePrompt:
      "Build an automation that scrapes weekly pricing from competitor sites, summarizes the trends, and sends a report to my WhatsApp every Monday.",
  },
  {
    id: "sales-person",
    title: "Sales Outreach & Follow-up",
    icon: "💼",
    bullets: [
      "Automates outreach and follows up with prospects over email or chat",
      "Handles objections, answers product questions, and qualifies intent",
      "Books qualified meetings directly on your calendar",
      "Best for: Sales teams scaling outreach without adding manual legwork",
    ],
    accentColor: "text-emerald-500",
    category: "front",
    examplePrompt:
      "Automate outbound follow-ups with inbound leads. Follow up over email, handle objections, and send a booking link when they are ready.",
  },
  {
    id: "other",
    title: "Custom Automation",
    icon: "✨",
    bullets: [
      "Have a specific business or personal workflow in mind?",
      "I'll interview you to understand what you want to automate",
      "We'll configure the right integrations and channels together",
    ],
    accentColor: "text-gray-500",
    showOnLanding: false,
  },
];
